import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Campaign } from "../models/Campaign";
import {
	getMillHeatList,
	getMillHeatResults,
} from "../repositories/heatListRepository";

export const useHeatListData = ({
	start,
	end,
	onError,
}: {
	start: Date;
	end: Date;
	onError?: (error: Error) => void;
}) => {
	const { data, isLoading } = useQuery({
		queryKey: ["millHeatList", start.getTime(), end.getTime()],
		queryFn: ({ signal }) => getMillHeatList({ start, end }, signal),
		refetchInterval: 60000,
		onError,
	});

	const rows = useMemo(() => {
		if (!data?.campaigns) return [];
		const delaysMap = new Map(data.delays?.map((d) => [d.CampaignId, d]) ?? []);
		return data.campaigns.map((campaign) => {
			const delayData = campaign.CampaignId
				? delaysMap.get(campaign.CampaignId)
				: undefined;
			return {
				...campaign,
				TotalDelayTime: delayData?.TotalDelayTime ?? 0,
				DelayTimePercent: delayData?.DelayTimePercent ?? 0,
			} as Campaign;
		});
	}, [data]);

	return { rows, isLoading };
};

export const useHeatResults = ({
	heatId,
	onError,
}: {
	heatId: number | null;
	onError?: (error: Error) => void;
}) => {
	const { data, isLoading } = useQuery({
		queryKey: ["millHeatResults", heatId],
		queryFn: ({ signal }) =>
			getMillHeatResults({ heatId: heatId as number }, signal),
		enabled: heatId != null,
		onError,
	});

	return {
		results: data?.results,
		dispositions: data?.dispositions,
		isLoading,
	};
};
